import { useNavigate } from 'react-router-dom';
import { HomeSection } from '@/services/homeSections';
import { withImageFallback } from '@/utils/images';

function AnimatedText({ text, baseDelay, className }: { text: string; baseDelay: number; className?: string }) {
  return (
    <span className={className}>
      {text.split('').map((char, i) => (
        <span
          key={i}
          className="animate-word-reveal inline-block"
          style={{ animationDelay: `${baseDelay + i * 0.04}s` }}
        >
          {char}
        </span>
      ))}
    </span>
  );
}

type HeroSectionProps = {
  section?: HomeSection | null;
};

const fallbackHero = {
  eyebrow: '聽見生活的細節',
  title: '從今天開始',
  subtitle: '讓對話重新靠近',
  description: '專業聽力師陪你了解聽力狀況，找到適合生活節奏的聽力照護方案。',
  primaryLabel: '免費聽力檢測',
  primaryUrl: '/hearing-test',
  secondaryLabel: '預約諮詢',
  secondaryUrl: '/booking',
  note: '約 3 分鐘完成基本資料填寫，專人將協助安排服務。',
  image: 'https://readdy.ai/api/search-image?query=warm%20caring%20scene%20of%20elderly%20person%20being%20assisted%20with%20hearing%20aid%20by%20professional%20audiologist%20in%20modern%20bright%20clinic%20soft%20natural%20lighting%20green%20and%20orange%20color%20tones%20professional%20photography%20high%20quality&width=960&height=680&seq=30&orientation=landscape',
  imageAlt: '聽力師協助長者配戴助聽器',
};

function splitTitle(title: string, subtitle?: string | null) {
  if (subtitle) return [title, subtitle];
  const parts = title.split(/[，,]/).map((part) => part.trim()).filter(Boolean);
  return parts.length >= 2 ? [parts[0], parts.slice(1).join('，')] : [title, ''];
}

export default function HeroSection({ section }: HeroSectionProps) {
  const navigate = useNavigate();
  const [titleLine1, titleLine2] = splitTitle(section?.Title || fallbackHero.title, section?.Subtitle);
  const primaryUrl = section?.PrimaryButtonUrl || fallbackHero.primaryUrl;
  const secondaryUrl = section?.SecondaryButtonUrl || fallbackHero.secondaryUrl;
  const heroTags = section?.Tags?.filter(Boolean) || [];

  function go(url: string) {
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(url);
  }

  return (
    <section className="relative min-h-[540px] md:min-h-[820px] flex items-center bg-white overflow-hidden">
      <div className="w-full md:w-[50%] px-5 md:px-10 lg:px-16 pt-20 md:pt-28 pb-8 md:pb-12 flex flex-col justify-center z-10">
        {section?.BadgeText && (
          <div className="mb-3 animate-fade-in-up delay-200">
            <span className="inline-flex items-center rounded-full bg-accent-100 px-3 py-1.5 text-xs font-bold text-accent-700 ring-1 ring-accent-200">
              {section.BadgeText}
            </span>
          </div>
        )}
        <span className="inline-block text-xs font-semibold tracking-widest text-accent-600 uppercase mb-3 animate-fade-in-up delay-300">
          {section?.Eyebrow || fallbackHero.eyebrow}
        </span>

        <h1 className="text-[1.75rem] leading-snug md:text-4xl lg:text-5xl md:leading-loose font-bold text-foreground-900">
          <span className="block mb-1 md:mb-2">
            <AnimatedText text={titleLine1} baseDelay={0} />
          </span>
          {titleLine2 && (
            <span className="block text-accent-600">
              <AnimatedText text={titleLine2} baseDelay={0.36} />
            </span>
          )}
        </h1>
        <p className="mt-3 md:mt-6 text-foreground-600 text-base md:text-lg leading-relaxed max-w-md animate-fade-in-up delay-600">
          {section?.Description || fallbackHero.description}
        </p>
        {heroTags.length > 0 && (
          <div className="mt-4 flex max-w-md flex-wrap gap-2 animate-fade-in-up delay-600">
            {heroTags.map((tag) => (
              <span className="rounded-full border border-background-200 bg-white px-3 py-1.5 text-xs font-bold text-foreground-600 shadow-sm" key={tag}>
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-5 md:mt-8 w-full max-w-md animate-fade-in-up delay-700">
          <button
            type="button"
            onClick={() => go(primaryUrl)}
            className="w-full group relative overflow-hidden rounded-xl bg-primary-800 text-white px-6 py-4 md:py-5 text-base font-medium hover:bg-primary-900 transition-all duration-300 flex items-center justify-center gap-3"
          >
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 group-hover:bg-white/20 transition-all duration-300">
              <i className="ri-headphone-line text-lg"></i>
            </span>
            <span className="whitespace-nowrap">{section?.PrimaryButtonLabel || fallbackHero.primaryLabel}</span>
            <i className="ri-arrow-right-line text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all duration-300"></i>
          </button>
          <p className="mt-2 text-xs text-foreground-500 text-center">
            {section?.NoteText || fallbackHero.note}
          </p>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 animate-fade-in-up delay-800">
          <button
            type="button"
            onClick={() => go(secondaryUrl)}
            className="bg-accent-500 text-background-50 px-6 md:px-7 py-3 md:py-3.5 rounded-xl text-base font-medium hover:bg-accent-600 transition-all duration-200 whitespace-nowrap"
          >
            {section?.SecondaryButtonLabel || fallbackHero.secondaryLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById('knowledge-section');
              if (!el) return;
              const offset = 100;
              const target = el.getBoundingClientRect().top + window.scrollY - offset;
              const start = window.scrollY;
              const distance = target - start;
              const duration = 1200;
              let startTime: number | null = null;
              const animate = (currentTime: number) => {
                if (!startTime) startTime = currentTime;
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const ease = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                window.scrollTo(0, start + distance * ease);
                if (progress < 1) requestAnimationFrame(animate);
              };
              requestAnimationFrame(animate);
            }}
            className="border border-background-200 text-foreground-600 px-6 md:px-7 py-3 md:py-3.5 rounded-xl text-base font-medium hover:border-accent-400 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-volume-up-line text-accent-600"></i>
            聽力知識
          </button>
        </div>
      </div>

      <div className="hidden md:block md:w-[50%] absolute right-0 top-0 bottom-0 animate-fade-in delay-500">
        <img
          src={withImageFallback(section?.ImageUrl || fallbackHero.image)}
          alt={section?.ImageAlt || fallbackHero.imageAlt}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/40 to-transparent" />
      </div>
    </section>
  );
}
